// Wolt Drive, as the guest's checkout and order view see it.
//
// Pure rules only. RestaurantMenu is ~4,800 lines and eslint-plugin-react-hooks
// 4.3.0 gives up analysing it once the component body carries a few more
// branches (see the note in utils/stampcard.js), so every decision that can
// live outside it does — which is also what makes these testable at all.

import moment from 'moment';

// Wolt keeps a shipment promise for 60 minutes. Ours are treated as gone ten
// minutes earlier, so a guest who sits on the payment step cannot hand the
// backend a promise that lapses between our check and the booking.
export const WOLT_QUOTE_MAX_AGE_MS = 50 * 60 * 1000;

// No error. A frozen constant so clearing it never hands the render tree a
// fresh object identity.
export const NO_WOLT_ERROR = Object.freeze({ error: false, message: '' });

/**
 * Is this quote still one we may charge and book against?
 *
 * A quote without an id is not a quote, and one with no fetched-at time — an
 * older persisted cart, say — cannot prove its age, so it counts as expired.
 *
 * @param {object} quote      the /woltDelivery/check answer
 * @param {number} fetchedAt  ms since epoch when it arrived
 * @param {number} now        injectable clock, for tests
 */
export const isWoltQuoteFresh = (quote, fetchedAt, now = Date.now()) => {
  if (!quote?.id) return false;
  const at = Number(fetchedAt);
  if (!Number.isFinite(at) || at <= 0) return false;
  return now - at < WOLT_QUOTE_MAX_AGE_MS;
};

/**
 * The fee this quote charges the guest, in major units, or '' when there is
 * no quote. customer_price is server-computed and may be venue-subsidized, so
 * it wins over Wolt's own price.
 */
export const woltQuoteFee = (quote) => {
  const feeMinor = quote?.customer_price?.amount ?? quote?.price?.amount;
  return feeMinor != null ? feeMinor / 100 : '';
};

/**
 * Did the fee change between what the guest was shown and a fresh quote?
 * A missing fee on either side counts as a change: the guest has not seen
 * that price.
 */
export const woltFeeChanged = (shownFee, freshFee) => {
  if (shownFee === '' || shownFee == null || freshFee === '' || freshFee == null) return true;
  return Math.round(Number(shownFee) * 100) !== Math.round(Number(freshFee) * 100);
};

/**
 * Is the delivery fee still unknown — delivery chosen, no quote yet? Then so is
 * the total: the checkout shows a placeholder for both rather than a smaller
 * number that reads as the price (the app's woltFeePending).
 */
export const isWoltFeePending = (orderType, restaurant, woltDeliveryFee) =>
  orderType === 'delivery' &&
  Boolean(restaurant?.EnableWoltDelivery) &&
  (woltDeliveryFee === '' || woltDeliveryFee == null);

/**
 * The delivery slot a quote was asked for: null for ASAP, otherwise the local
 * date and time. Lets the page tell whether a date/time change has outdated the
 * quote it holds, without throwing away one that still matches.
 */
export const woltQuoteSlot = (pickupType, pickupDate, pickupTime) => {
  if (pickupType !== 'later' || !pickupDate || !pickupTime) return null;
  return `${moment(pickupDate).format('YYYY-MM-DD')} ${pickupTime}`;
};

/**
 * The Wolt keys of an order payload. Every checkout path builds its payload
 * through this, so a pickup order can never carry a shipment a guest fetched
 * before switching away from delivery, and `isDelivery` always travels — it is
 * what lets the backend refuse a delivery order that lost its quote.
 */
export const woltOrderFields = ({
  orderType,
  restaurant,
  woltDelivery,
  deliveryComment,
  deliveryAddress,
}) => {
  const isDelivery = orderType === 'delivery';
  const withShipment = isDelivery && Boolean(restaurant?.EnableWoltDelivery && woltDelivery);

  return {
    isDelivery,
    woltShipment: withShipment ? woltDelivery : null,
    deliveryComment: withShipment ? deliveryComment || '' : '',
    //raw typed address — persisted on the order for Wolt promise re-quotes
    deliveryAddress: isDelivery ? deliveryAddress || null : null,
  };
};

/**
 * What to tell the guest when /woltDelivery/check failed — in their language,
 * never the backend's English `message` ("Delivery address is outside the 5 km
 * delivery radius") nor axios's "Request failed with status code 502". The
 * status decides the sentence, as in the app (hey_waitr describeQuoteError).
 * The minimum-order 400 and the closed 409 are said before this is asked, by
 * woltMinOrderRejection and woltClosedRejection.
 *
 *   no response  the request never got out: the guest's connection
 *   400          outside the venue's delivery radius, or an address it cannot use
 *   404          not deliverable here now: no courier, outside Wolt's area
 *   5xx          an outage of ours or Wolt's — never "your address is wrong"
 *
 * @param {object} error    the axios error
 * @param {object} strings  translations[language]
 */
export const woltQuoteErrorMessage = (error, strings) => {
  const response = error?.response;
  if (!response) return strings?.delivery_quote_error_offline;

  const status = Number(response.status) || 0;
  // A refusal is a JSON object; the endpoint's own 500 answers a bare string.
  const data = response.data && typeof response.data === 'object' ? response.data : {};

  if (status === 400) {
    const radiusKm = Number(data.radiusKm) || 0;
    return radiusKm > 0
      ? strings?.delivery_quote_error_radius?.replace('{radius}', String(radiusKm))
      : strings?.delivery_quote_error_address;
  }
  if (status === 404) return strings?.delivery_quote_error_no_courier;
  if (status >= 500) return strings?.wolt_quote_error;
  return strings?.delivery_quote_missing;
};

/**
 * The venue-closed refusals of /woltDelivery/check, in the guest's language.
 *
 * The backend answers both with 409 and a bare English `message`:
 *   "Restaurant is closed at the selected pickup time"  (a scheduled slot)
 *   "Restaurant is currently closed"                    (an ASAP quote)
 * which would otherwise reach a Danish guest verbatim.
 * Returns null for anything else, so the caller falls through to it.
 *
 * @param {object} error         the axios error
 * @param {object} strings       translations[language]
 */
export const woltClosedRejection = (error, strings) => {
  if (error?.response?.status !== 409) return null;
  const message = String(error.response.data?.message || error.response.data?.Message || '');

  if (/closed at the selected/i.test(message)) {
    return strings?.wolt_closed_at_selected_time || null;
  }
  if (/currently closed/i.test(message)) {
    return strings?.wolt_closed_now || null;
  }
  return null;
};

// Wolt statuses after which no courier is coming.
const FAILED_STATUSES = ['rejected', 'cancelled', 'customer_no_show'];

// The first source that has the key. The status payload and the cached order
// both carry Wolt fields, and the status payload is the newer of the two.
const pick = (sources, key) => {
  for (const source of sources) {
    if (source && !Array.isArray(source) && source[key] != null && source[key] !== '') {
      return source[key];
    }
  }
  return null;
};

/**
 * Has the delivery of this order failed?
 *
 * `woltDeliveryState` is the backend's own summary when it serves one. Without
 * it, a terminal Wolt status or the venue's "Annuller Wolt" timestamp says the
 * same thing.
 */
export const isWoltDeliveryFailed = (...sources) => {
  const state = pick(sources, 'woltDeliveryState');
  if (state === 'failed') return true;
  if (state === 'pending' || state === 'confirmed') return false;
  return (
    Boolean(pick(sources, 'woltDeliveryCancelledAt')) ||
    FAILED_STATUSES.includes(pick(sources, 'woltDeliveryStatus'))
  );
};

/**
 * How a failed delivery ended, for the words the guest is given: 'cancelled'
 * when it was called off — the venue's "Annuller Wolt", or Wolt's own cancel —
 * otherwise 'failed' (refused, or the courier could not hand it over). Same
 * precedence as the app's orderDeliveryState: a rejection or no-show first.
 */
export const woltFailureKind = (...sources) => {
  const status = pick(sources, 'woltDeliveryStatus');
  if (status === 'rejected' || status === 'customer_no_show') return 'failed';
  if (status === 'cancelled' || pick(sources, 'woltDeliveryCancelledAt')) return 'cancelled';
  return 'failed';
};

// The line the order view shows for a failed delivery (the app's
// ORDER_DELIVERY_STATE_LABEL_KEYS). Neither promises a refund.
export const woltFailureMessageKey = (...sources) =>
  woltFailureKind(...sources) === 'cancelled'
    ? 'delivery_status_cancelled'
    : 'delivery_status_failed';

/**
 * Everything the tracking UI needs to know about an order's delivery.
 *
 * @param {...object} sources  newest first, e.g. (payConfirm.order, payConfirm)
 * @returns {{state: 'none'|'pending'|'confirmed'|'failed', trackingUrl: string|null, eta: string|null}}
 *          `failed` and `pending` never carry a link or an ETA: a failed
 *          delivery's link is dead, and a pending one has no courier yet.
 */
export const woltDeliveryView = (...sources) => {
  const none = { state: 'none', trackingUrl: null, eta: null };

  if (isWoltDeliveryFailed(...sources)) return { ...none, state: 'failed' };

  const state = pick(sources, 'woltDeliveryState');
  if (state === 'pending') return { ...none, state: 'pending' };

  const accept = pick(sources, 'woltDeliveryAccept');
  const trackingUrl =
    pick(sources, 'woltTrackingLink') ||
    pick(sources, 'woltDeliveryTrackingUrl') ||
    accept?.tracking?.url ||
    null;
  const eta = pick(sources, 'woltDeliveryDropoffETA');

  if (state === 'confirmed' || trackingUrl || eta) {
    return { state: 'confirmed', trackingUrl, eta };
  }
  return none;
};

/**
 * Does this status payload carry anything the delivery view shows? The order
 * poller merges it into the modal's state whenever it does.
 */
export const hasWoltDeliveryUpdate = (status) =>
  Boolean(
    status &&
      (status.woltDeliveryAccept ||
        status.woltDeliveryDropoffETA ||
        status.woltDeliveryTrackingUrl ||
        status.woltTrackingLink ||
        status.woltDeliveryState ||
        status.woltDeliveryStatus ||
        status.woltDeliveryCancelledAt),
  );

// The RefundReason the backend writes when Wolt refused a held order.
export const WOLT_NO_COURIER = 'wolt_no_courier';

/**
 * Was this order refunded because the delivery could not happen? Both
 * "wolt_no_courier" and "wolt_no_courier_zero_value" (nothing was charged)
 * say so — the app reads them the same way (hey_waitr orders/refunded.ts).
 */
export const isDeliveryRefund = (reason) =>
  typeof reason === 'string' && reason.startsWith(WOLT_NO_COURIER);

// The sentence a refund is told with: the delivery one when Wolt was why,
// the plain one for a refund the venue made itself.
export const refundMessageKey = (reason) =>
  isDeliveryRefund(reason) ? 'order_refunded_message' : 'order_refunded_message_generic';
